import { ReactElement } from "react";
import { DriverMarker, DropoffMarker, PickupMarker } from "../markers";
import { EtaMarkerOverlay } from "../overlays";
import { MapMarker } from "../MapView.types";
import { DRIVER_SIZE, ETA_BLOCK_HEIGHT, ETA_BLOCK_WIDTH, IconAnchorStyle, PIN_SIZE } from "./markerLayout";

export type MarkerNode = {
  node: ReactElement;
  size: [number, number];
  anchor: IconAnchorStyle;
};

/**
 * Builds the actual visual tree for one marker — pin (+ optional ETA pill
 * stacked above it) or driver avatar — along with the pixel size and anchor
 * style needed to position it correctly. This is the one place that decides
 * "what a marker looks like"; both adapters just host whatever this returns
 * through their own SDK (`OverlayView` for Google, a `divIcon` built via
 * `createLeafletIcon` for Leaflet) instead of each reimplementing the
 * pin/ETA layout independently and risking the two drifting apart.
 */
export function buildMarkerNode(marker: MapMarker, etaLabel: string | null): MarkerNode {
  if (marker.kind === "driver") {
    return {
      node: <DriverMarker name={marker.name} avatarUrl={marker.avatarUrl} />,
      size: DRIVER_SIZE,
      anchor: "center",
    };
  }

  const pin = marker.kind === "pickup" ? <PickupMarker /> : <DropoffMarker sequence={marker.sequence} />;

  if (!etaLabel) {
    return { node: pin, size: PIN_SIZE, anchor: "bottom-center" };
  }

  const totalWidth = Math.max(PIN_SIZE[0], ETA_BLOCK_WIDTH);
  const totalHeight = PIN_SIZE[1] + ETA_BLOCK_HEIGHT;

  const node = (
    <div style={{ position: "relative", width: totalWidth, height: totalHeight }}>
      <div style={{ position: "absolute", bottom: 0, left: "50%", transform: "translateX(-50%)" }}>{pin}</div>
      <div
        style={{
          position: "absolute",
          top: 0,
          left: "50%",
          transform: "translateX(-50%)",
          width: ETA_BLOCK_WIDTH,
          display: "flex",
          justifyContent: "center",
        }}
      >
        <EtaMarkerOverlay label={etaLabel} />
      </div>
    </div>
  );

  return { node, size: [totalWidth, totalHeight], anchor: "bottom-center" };
}
